import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import Divider from '../Divider'
import InputField from '../InputField'
import WaveLoading from 'components/WaveLoading'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import api from 'containers/api'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'

import './GooglePlayBucket.scss'

const Guide = ({ onHide, show }) => {
  return (
    <div className={`guide ${show ? 'show' : ''}`}>
      <button onClick={() => onHide()} className="close-button">
        &times;
      </button>
      <h1>Custom App Setup Guide</h1>
      <Divider spaceY={10} />

      <div className="step-heading">
        <p>STEP 1</p>
        <h2>Create a Custom App</h2>
      </div>
      <ol>
        <li>Go to <a href="https://dev.shopify.com/" target="_blank" rel="noopener noreferrer"><b>dev.shopify.com</b></a></li>
        <li>Log in with your Shopify account</li>
        <li>Click <b>Create an app</b></li>
        <li>Enter a name (e.g., "Maya Insights Integration")</li>
        <li>Click <b>Create app</b></li>
      </ol>

      <div className="step-heading">
        <p>STEP 2</p>
        <h2>Configure API Scopes</h2>
      </div>
      <ol>
        <li>In your app's configuration, set the following <b>Admin API access scopes</b>:
          <ul style={{ marginTop: '8px' }}>
            <li><code>read_orders</code></li>
            <li><code>read_all_orders</code></li>
            <li><code>read_customers</code></li>
            <li><code>read_products</code></li>
            <li><code>read_inventory</code></li>
            <li><code>read_fulfillments</code></li>
            <li><code>read_merchant_managed_fulfillment_orders</code></li>
            <li><code>read_marketing_events</code></li>
          </ul>
        </li>
        <li>Save the configuration</li>
      </ol>

      <div className="step-heading">
        <p>STEP 3</p>
        <h2>Install the App on Your Store</h2>
      </div>
      <ol>
        <li>In your app settings, install the app on the target store</li>
        <li>Approve the requested permissions when prompted</li>
        <li>Once installed, copy the <b>Client ID</b> and <b>Client Secret</b> from the app's credentials page</li>
      </ol>

      <div className="step-heading">
        <p>STEP 4</p>
        <h2>Connect to Maya Insights</h2>
      </div>
      <ol>
        <li>Enter your store name below (e.g., <code>yourstore</code> from <code>yourstore.myshopify.com</code>)</li>
        <li>Paste the <b>Client ID</b> and <b>Client Secret</b></li>
        <li>Click <b>Connect</b></li>
        <li>Your data will sync automatically!</li>
      </ol>
    </div>
  )
}

const Shopify = () => {
  const { id: serviceUUID } = useParams<{ id: string }>()
  const { currentOrganization } = useContext(OrganizationsContext)
  const token = useSelector(selectToken)

  const [showGuide, setShowGuide] = useState(false)
  const [storeName, setStoreName] = useState<string>('')
  const [clientId, setClientId] = useState<string>('')
  const [clientSecret, setClientSecret] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)


  const handleConnect = async () => {
    if (!storeName) {
      setError('Please enter your store name')
      return
    }

    if (!clientId) {
      setError('Please enter your Client ID')
      return
    }

    if (!clientSecret) {
      setError('Please enter your Client Secret')
      return
    }

    if (!currentOrganization || !currentOrganization.slug) {
      setError('Organization not loaded. Please refresh the page.')
      return
    }

    setError(null)
    setLoading(true)

    try {
      // Clean up store name - remove .myshopify.com if user included it
      let cleanStoreName = storeName.trim()
        .replace(/^https?:\/\//, '')
        .replace(/\/$/, '')
        .replace('/admin', '')
        .replace('.myshopify.com', '')

      // Call generic API credentials connect endpoint
      const response = await api.post(`/services/${serviceUUID}/credentials/connect`, {
        credentials: {
          store_name: cleanStoreName,
          client_id: clientId.trim(),
          client_secret: clientSecret.trim()
        }
      }, api.getHeaders(token), { organization_id: currentOrganization.id })

      const data = response.data || response

      // Check for successful connection (credentials_id indicates success)
      if (data.credentials_id) {
        setSuccess(true)
        setError(null)
        // Redirect to sources page after 2 seconds
        setTimeout(() => {
          window.location.href = `/organization/${currentOrganization.slug}/sources`
        }, 2000)
      } else {
        setError(data.message || 'Failed to connect. Please check your credentials and try again.')
        setLoading(false)
      }

    } catch (err: any) {
      console.error('Shopify connect error:', err)
      const errorMessage = err.response?.data?.detail?.message ||
                          err.response?.data?.message ||
                          'Failed to connect. Please verify your credentials and that the app is installed on your store.'
      setError(errorMessage)
      setLoading(false)
    }
  }

  const handleFieldChange = (setter: (v: string) => void) => (value: string) => {
    setter(value)
    if (error) setError(null)
  }

  return (
    <div className="guide-container">
      <Guide onHide={() => setShowGuide(false)} show={showGuide} />

      <div style={{ paddingLeft: '20px', paddingBottom: '5rem' }}>
        <img
          src="https://old.mayainsights.com/static/img/logos/shopify.svg"
          width="150"
          alt="Shopify"
          onError={(e) => {
            // Fallback to PNG if SVG doesn't exist
            e.currentTarget.src = 'https://www.mayainsights.com/wp-content/uploads/shopify-logo.png'
          }}
        />

        <h2 style={{ fontWeight: 'normal', marginTop: '1rem' }}>Connect Your Shopify Store</h2>
        <p style={{ color: '#9b9b9b', fontSize: '14px' }}>
          First, create a custom app on <a href="https://dev.shopify.com/" target="_blank" rel="noopener noreferrer">dev.shopify.com</a> and install it on your store. Press the{' '}
          <button
            style={{ verticalAlign: 'initial' }}
            className="button-tip"
            onClick={() => setShowGuide(true)}
          >
            Guide
          </button>{' '}
          button for detailed instructions.
        </p>

        <Divider spaceY={20} />

        {success ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            width: '100%',
            maxWidth: '600px',
            padding: '2rem',
            backgroundColor: '#f0f9ff',
            border: '2px solid #22c55e',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '48px', color: '#22c55e' }}>✓</div>
            <h3 style={{ color: '#22c55e', margin: 0 }}>Successfully Connected!</h3>
            <p style={{ color: '#666', textAlign: 'center', margin: 0 }}>
              Your Shopify store is now connected. Redirecting...
            </p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '1rem',
            width: '100%',
            maxWidth: '600px'
          }}>
            <h3 style={{ fontWeight: 'normal', marginBottom: 0 }}>Store Name</h3>
            <p style={{ color: '#9b9b9b', fontSize: '14px', margin: '0' }}>
              Enter your store name (e.g., <code>yourstore</code> from <code>yourstore.myshopify.com</code>)
            </p>

            <InputField
              type="text"
              label=""
              placeholder="yourstore"
              value={storeName}
              setValue={handleFieldChange(setStoreName)}
              style={{
                border: error ? '1px solid #ff4444' : undefined,
                width: '100%'
              }}
              disabled={loading}
            />

            <h3 style={{ fontWeight: 'normal', marginBottom: 0, marginTop: '1rem' }}>Client ID</h3>
            <p style={{ color: '#9b9b9b', fontSize: '14px', margin: '0' }}>
              The Client ID from your app on <a href="https://dev.shopify.com/" target="_blank" rel="noopener noreferrer">dev.shopify.com</a>
            </p>

            <InputField
              type="text"
              label=""
              placeholder="your-client-id"
              value={clientId}
              setValue={handleFieldChange(setClientId)}
              style={{
                border: error ? '1px solid #ff4444' : undefined,
                width: '100%',
                fontFamily: 'monospace'
              }}
              disabled={loading}
            />

            <h3 style={{ fontWeight: 'normal', marginBottom: 0, marginTop: '1rem' }}>Client Secret</h3>
            <p style={{ color: '#9b9b9b', fontSize: '14px', margin: '0' }}>
              The Client Secret from your app on <a href="https://dev.shopify.com/" target="_blank" rel="noopener noreferrer">dev.shopify.com</a>
            </p>

            <InputField
              type="password"
              label=""
              placeholder="your-client-secret"
              value={clientSecret}
              setValue={handleFieldChange(setClientSecret)}
              style={{
                border: error ? '1px solid #ff4444' : undefined,
                width: '100%',
                fontFamily: 'monospace'
              }}
              disabled={loading}
            />

            <button
              className="submit-button"
              onClick={handleConnect}
              disabled={!storeName || !clientId || !clientSecret || loading}
              style={{
                margin: 0,
                width: '100%',
                opacity: (!storeName || !clientId || !clientSecret || loading) ? 0.5 : 1
              }}
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>

            {error && (
              <div style={{
                color: '#ff4444',
                fontSize: '14px',
                padding: '0.75rem',
                backgroundColor: '#fff5f5',
                border: '1px solid #ffcccc',
                borderRadius: '4px',
                width: '100%'
              }}>
                {error}
              </div>
            )}

            {loading && (
              <div style={{ width: '100%', textAlign: 'center', padding: '1rem' }}>
                <WaveLoading barColor={"green"}/>
                <p style={{ color: '#9b9b9b', fontSize: '14px', marginTop: '1rem'}}>
                  Validating credentials and connecting...
                </p>
              </div>
            )}

            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#666',
              width: '100%',
              marginTop: '1rem'
            }}>
              <strong>Note:</strong> Your credentials are securely encrypted and stored. We only request read permissions to sync your orders, customers, and product data.
              Make sure the app is installed on your store before connecting.
            </div>
          </div>
        )}
      </div>

      <div className="guide-bar">
        <button
          className="submit-button guide-button"
          onClick={() => setShowGuide(true)}
        >
          <span>Guide</span>
          <span className="material-icons-outlined saved-icon non-selectable">
            menu_book
          </span>
        </button>
      </div>
    </div>
  )
}

export default Shopify
